import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Iconify from "../../../components/iconify";
import { toCurrency } from "./shopUtils";

const buildVariantLabel = (variant) => {
  if (!variant) return "Standard";
  const optionParts = (variant.optionValues || [])
    .map((opt) => `${opt.name}: ${opt.value}`)
    .join(", ");
  return optionParts || variant.label || "Standard";
};

const getActiveVariants = (product) =>
  Array.isArray(product?.variants)
    ? product.variants.filter((v) => v?.isActive !== false)
    : [];

const buildProductVariantOptions = (products = []) => {
  const options = [];
  for (const product of products) {
    if (!product?.isActive) continue;
    const variants = getActiveVariants(product);
    for (const variant of variants) {
      options.push({
        productId: String(product._id),
        productName: product.name,
        variantKey: variant.key,
        variantLabel: buildVariantLabel(variant),
        price:
          typeof variant.price === "number" ? variant.price : product.basePrice,
        stock: variant.stock,
        label: `${product.name} — ${buildVariantLabel(variant)}`,
      });
    }
  }
  return options;
};

export default function ShopOrderEditDialog({
  open,
  order,
  products = [],
  onSave,
  onClose,
  saving,
  error,
}) {
  const [editItems, setEditItems] = useState([]);
  const [addPickerValue, setAddPickerValue] = useState(null);

  useEffect(() => {
    if (open && order?.lineItems?.length) {
      setEditItems(
        order.lineItems.map((item) => ({
          productId: String(item.productId),
          productName: item.productName,
          variantKey: item.variantKey,
          variantLabel: item.variantLabel || "Standard",
          quantity: item.quantity,
          unitPrice: item.unitPrice,
        })),
      );
      setAddPickerValue(null);
    }
  }, [open, order]);

  const catalogOptions = useMemo(
    () => buildProductVariantOptions(products),
    [products],
  );

  const handleQuantityChange = (index, value) => {
    const parsed = parseInt(value, 10);
    if (Number.isNaN(parsed) || parsed < 1) return;
    setEditItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, quantity: parsed } : item)),
    );
  };

  const handleRemoveItem = (index) => {
    setEditItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddItem = (option) => {
    if (!option) return;

    const existingIndex = editItems.findIndex(
      (item) =>
        item.productId === option.productId &&
        item.variantKey === option.variantKey,
    );

    if (existingIndex >= 0) {
      setEditItems((prev) =>
        prev.map((item, i) =>
          i === existingIndex
            ? { ...item, quantity: item.quantity + 1 }
            : item,
        ),
      );
    } else {
      setEditItems((prev) => [
        ...prev,
        {
          productId: option.productId,
          productName: option.productName,
          variantKey: option.variantKey,
          variantLabel: option.variantLabel,
          quantity: 1,
          unitPrice: option.price,
        },
      ]);
    }

    setAddPickerValue(null);
  };

  const newTotal = useMemo(
    () =>
      editItems.reduce(
        (sum, item) => sum + (item.unitPrice || 0) * (item.quantity || 0),
        0,
      ),
    [editItems],
  );

  const hasChanges = useMemo(() => {
    if (!order?.lineItems) return false;
    if (editItems.length !== order.lineItems.length) return true;
    return editItems.some((item, i) => {
      const orig = order.lineItems[i];
      return (
        item.productId !== String(orig.productId) ||
        item.variantKey !== orig.variantKey ||
        item.quantity !== orig.quantity
      );
    });
  }, [editItems, order]);

  const webhookAlreadySent = Boolean(order?.fulfillment?.notifiedAt);

  const handleSave = () => {
    onSave({
      id: order._id,
      lineItems: editItems.map((item) => ({
        productId: item.productId,
        variantKey: item.variantKey,
        quantity: item.quantity,
      })),
    });
  };

  if (!order) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Bestellung bearbeiten
      </DialogTitle>

      <DialogContent dividers>
        {webhookAlreadySent && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Diese Bestellung wurde bereits an das Versandsystem gemeldet. Beim
            Speichern wird eine Aktualisierung gesendet.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error?.data?.message || "Fehler beim Speichern der Bestellung."}
          </Alert>
        )}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Produkt</TableCell>
                <TableCell>Variante</TableCell>
                <TableCell align="right">Einzelpreis</TableCell>
                <TableCell align="center" sx={{ width: 100 }}>
                  Menge
                </TableCell>
                <TableCell align="right">Summe</TableCell>
                <TableCell align="center" sx={{ width: 50 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {editItems.map((item, index) => (
                <TableRow key={`${item.productId}-${item.variantKey}-${index}`}>
                  <TableCell>
                    <Typography variant="body2">{item.productName}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={item.variantLabel} />
                  </TableCell>
                  <TableCell align="right">
                    {toCurrency(item.unitPrice)}
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      type="number"
                      size="small"
                      value={item.quantity}
                      onChange={(e) =>
                        handleQuantityChange(index, e.target.value)
                      }
                      inputProps={{ min: 1, style: { textAlign: "center" } }}
                      sx={{ width: 70 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {toCurrency(item.unitPrice * item.quantity)}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Entfernen">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveItem(index)}
                      >
                        <Iconify icon="eva:trash-2-outline" width={18} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}

              {editItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      Keine Positionen vorhanden. Bitte mindestens eine Position
                      hinzufügen.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ my: 2 }} />

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
        >
          <Autocomplete
            value={addPickerValue}
            onChange={(_, newValue) => handleAddItem(newValue)}
            options={catalogOptions}
            getOptionLabel={(option) => option.label || ""}
            isOptionEqualToValue={(option, value) =>
              option.productId === value.productId &&
              option.variantKey === value.variantKey
            }
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%" }}>
                  <Typography variant="body2" sx={{ flexGrow: 1 }}>
                    {option.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {toCurrency(option.price)}
                  </Typography>
                  {option.stock !== null && option.stock !== undefined && (
                    <Chip
                      size="small"
                      label={`${option.stock} Stk.`}
                      color={option.stock > 0 ? "default" : "error"}
                      variant="outlined"
                    />
                  )}
                </Stack>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                placeholder="Produkt hinzufügen..."
                label="Position hinzufügen"
              />
            )}
            sx={{ minWidth: 300, flexGrow: 1 }}
            noOptionsText="Keine Produkte gefunden"
          />

          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="subtitle1" fontWeight={600}>
              Gesamt:
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {toCurrency(newTotal)}
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" sx={{ minWidth: 100 }}>
          Abbrechen
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving || editItems.length === 0 || !hasChanges}
          sx={{ fontWeight: 600, minWidth: 120 }}
        >
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
}
